import React, { useRef, useEffect, useState } from 'react';
import { getColorForClass } from '../constants/colors';
import './DetectionOverlay.css';

const DetectionOverlay = ({ detections, imageWidth, imageHeight, videoWidth, videoHeight }) => {
  const overlayRef = useRef(null);
  const [overlayDimensions, setOverlayDimensions] = useState({ width: 0, height: 0 });

  // Get actual video display dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (overlayRef.current && overlayRef.current.parentElement) {
        const videoElement = overlayRef.current.parentElement.querySelector('video');
        if (videoElement) {
          const rect = videoElement.getBoundingClientRect();
          setOverlayDimensions({
            width: rect.width,
            height: rect.height
          });
        }
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  if (!detections || detections.length === 0) return null;

  // Use actual video display dimensions instead of video constraints
  const displayWidth = overlayDimensions.width || videoWidth;
  const displayHeight = overlayDimensions.height || videoHeight;

  // Calculate proper scale factors with aspect ratio consideration
  const imageAspectRatio = imageWidth / imageHeight;
  const displayAspectRatio = displayWidth / displayHeight;

  let scaleX, scaleY, offsetX = 0, offsetY = 0;

  if (imageAspectRatio > displayAspectRatio) {
    // Image is wider - fit to width, letterbox top/bottom
    scaleX = displayWidth / imageWidth;
    scaleY = scaleX; // Keep aspect ratio
    offsetY = (displayHeight - imageHeight * scaleY) / 2;
  } else {
    // Image is taller - fit to height, pillarbox left/right
    scaleY = displayHeight / imageHeight;
    scaleX = scaleY; // Keep aspect ratio
    offsetX = (displayWidth - imageWidth * scaleX) / 2;
  }

  return (
    <div className="detection-overlay" ref={overlayRef}>
      {detections.map((detection, index) => {
        // Apply proper scaling and offset
        const x = detection.x1 * scaleX + offsetX;
        const y = detection.y1 * scaleY + offsetY;
        const width = (detection.x2 - detection.x1) * scaleX;
        const height = (detection.y2 - detection.y1) * scaleY;
        const color = getColorForClass(detection.class_id);

        return (
          <div key={index}>
            {/* Detection box */}
            <div
              className="detection-box"
              style={{
                left: `${x}px`,
                top: `${y}px`,
                width: `${width}px`,
                height: `${height}px`,
                borderColor: color,
                boxShadow: `0 0 0 2px ${color}40`
              }}
            />
            
            {/* Label */}
            <div
              className="detection-label"
              style={{
                left: `${x}px`,
                top: `${Math.max(0, y - 30)}px`,
                backgroundColor: color,
                color: '#fff'
              }}
            >
              <span className="label-text">{detection.label}</span>
              <span className="confidence">{Math.round(detection.confidence * 100)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DetectionOverlay;
